import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Star, Plus, Pencil, Trash2, Save, X, Settings2, ExternalLink,
  RefreshCw, Search, MapPin
} from "lucide-react";
import { authGet, authPost, authPut, authDelete, isAdvisor } from "../utils/auth";
import { Button } from "@/components/ui/button";
import MobileBottomNav from "../components/MobileBottomNav";
import MobileSidebar from "../components/MobileSidebar";

const EMPTY_FORM = { reviewer_name: "", rating: 5, text: "", review_date: "" };

const StarPicker = ({ value, onChange }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map(i => (
      <button key={i} type="button" onClick={() => onChange(i)} data-testid={`star-pick-${i}`}>
        <Star className={`h-6 w-6 ${i <= value ? "text-[#A87608] fill-[#D4A017]" : "text-[#3A4557]"}`} />
      </button>
    ))}
  </div>
);

const ReviewForm = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial);
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <input
          type="text" placeholder="Reviewer name *" value={form.reviewer_name}
          onChange={e => setForm({ ...form, reviewer_name: e.target.value })}
          className="w-full px-3 py-2.5 bg-white border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-sm placeholder:text-[#3A4557] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/30"
          data-testid="review-name-input"
        />
        <input
          type="text" placeholder="Review date (e.g. May 2026)" value={form.review_date}
          onChange={e => setForm({ ...form, review_date: e.target.value })}
          className="w-full px-3 py-2.5 bg-white border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-sm placeholder:text-[#3A4557] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/30"
          data-testid="review-date-input"
        />
      </div>
      <textarea
        placeholder="Review text (paste from Google) *" value={form.text} rows={3}
        onChange={e => setForm({ ...form, text: e.target.value })}
        className="w-full px-3 py-2.5 bg-white border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-sm placeholder:text-[#3A4557] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/30"
        data-testid="review-text-input"
      />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <StarPicker value={form.rating} onChange={r => setForm({ ...form, rating: r })} />
        <div className="flex gap-2">
          <Button onClick={onCancel} variant="outline" className="btn-outline h-9" data-testid="review-cancel-btn">
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
          <Button onClick={() => onSave(form)} disabled={saving} className="btn-gold h-9" data-testid="review-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function WebsiteReviews() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({ google_rating: 5, google_review_count: 0, google_reviews_url: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [gcfg, setGcfg] = useState(null);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [bizQuery, setBizQuery] = useState("Kavach Box");
  const [candidates, setCandidates] = useState([]);
  const [gBusy, setGBusy] = useState(false);
  const [showKeySteps, setShowKeySteps] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);

  useEffect(() => {
    if (!isAdvisor()) { navigate("/advisor/dashboard"); return; }
    fetchData();
    fetchGoogleConfig();
  }, []);

  const fetchData = async () => {
    try {
      const res = await authGet("/api/site/reviews");
      if (res.ok) {
        setReviews(res.data?.reviews || []);
        setSettings(res.data?.settings || {});
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchGoogleConfig = async () => {
    try {
      const res = await authGet("/api/advisor/site-reviews/google-config");
      if (res.ok) setGcfg(res.data);
    } catch (e) { console.error(e); }
  };

  const saveApiKey = async () => {
    if (!apiKeyInput.trim()) return;
    setGBusy(true);
    try {
      const res = await authPut("/api/advisor/site-reviews/google-config", { api_key: apiKeyInput.trim() });
      if (res.ok) { toast.success("API key saved securely"); setApiKeyInput(""); fetchGoogleConfig(); }
      else toast.error(res.data?.detail || "Failed to save key");
    } catch { toast.error("Network error"); }
    setGBusy(false);
  };

  const findBusiness = async () => {
    if (!bizQuery.trim()) { toast.error("Enter your business name"); return; }
    setGBusy(true); setCandidates([]);
    try {
      const res = await authPost("/api/advisor/site-reviews/google-find", { query: bizQuery.trim() });
      if (res.ok) {
        const list = res.data?.candidates || [];
        if (list.length === 0) toast.error("No business found — try adding your city");
        setCandidates(list);
      } else toast.error(res.data?.detail || "Search failed");
    } catch { toast.error("Network error"); }
    setGBusy(false);
  };

  const selectBusiness = async (c) => {
    setGBusy(true);
    try {
      const res = await authPut("/api/advisor/site-reviews/google-config", {
        place_id: c.place_id, place_name: c.name, place_address: c.address
      });
      if (res.ok) { toast.success(`Linked: ${c.name}`); setCandidates([]); fetchGoogleConfig(); }
      else toast.error(res.data?.detail || "Failed to link business");
    } catch { toast.error("Network error"); }
    setGBusy(false);
  };

  const syncNow = async () => {
    setGBusy(true);
    try {
      const res = await authPost("/api/advisor/site-reviews/google-sync", {});
      if (res.ok) {
        toast.success(`Synced! ${res.data.imported} reviews imported · live rating ${res.data.rating} (${res.data.review_count} total)`);
        fetchData(); fetchGoogleConfig();
      } else toast.error(res.data?.detail || "Sync failed");
    } catch { toast.error("Network error"); }
    setGBusy(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await authPut("/api/advisor/site-reviews/settings", settings);
      if (res.ok) toast.success("Settings saved — landing page updated");
      else toast.error(res.data?.detail || "Failed to save settings");
    } catch { toast.error("Network error"); }
    setSaving(false);
  };

  const addReview = async (form) => {
    if (!form.reviewer_name.trim() || !form.text.trim()) { toast.error("Name and review text are required"); return; }
    setSaving(true);
    try {
      const res = await authPost("/api/advisor/site-reviews", form);
      if (res.ok) { toast.success("Review added"); setShowAdd(false); fetchData(); }
      else toast.error(res.data?.detail || "Failed to add review");
    } catch { toast.error("Network error"); }
    setSaving(false);
  };

  const updateReview = async (id, form) => {
    setSaving(true);
    try {
      const res = await authPut(`/api/advisor/site-reviews/${id}`, form);
      if (res.ok) { toast.success("Review updated"); setEditingId(null); fetchData(); }
      else toast.error(res.data?.detail || "Failed to update review");
    } catch { toast.error("Network error"); }
    setSaving(false);
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Remove this review from the website?")) return;
    try {
      const res = await authDelete(`/api/advisor/site-reviews/${id}`);
      if (res.ok) { toast.success("Review removed"); fetchData(); }
      else toast.error("Failed to remove review");
    } catch { toast.error("Network error"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#A87608] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-24">
      <header className="bg-white/80 backdrop-blur-xl sticky top-0 z-40 border-b border-[#C9D4E6]">
        <div className="px-4 py-4 flex items-center gap-3 max-w-3xl mx-auto">
          <button onClick={() => navigate(-1)} className="text-[#3A4557] hover:text-[#1B2A4A]" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-[#1B2A4A]">Website Reviews</h1>
            <p className="text-[#3A4557] text-xs">Google reviews shown on your landing page carousel</p>
          </div>
        </div>
      </header>

      <main className="px-4 py-5 max-w-3xl mx-auto space-y-5">
        {/* Live Google Sync */}
        <div className="glass-card p-5" data-testid="google-sync-card">
          <div className="flex items-center gap-2 mb-1">
            <RefreshCw className="h-4 w-4 text-[#A87608]" />
            <h2 className="text-[#1B2A4A] font-semibold text-sm">Live Google Review Sync</h2>
            {gcfg?.configured && gcfg?.place_id && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 font-medium" data-testid="google-sync-active-badge">Active</span>
            )}
          </div>
          <p className="text-[#3A4557] text-xs mb-4">
            Pulls your live Google rating, total review count and your 5 most relevant reviews straight from your Google Business profile. Auto-syncs daily at 8:30 AM.
          </p>

          {/* Step 1: API key */}
          <div className="mb-4">
            <label className="text-[#3A4557] text-xs block mb-1">
              Step 1 — Google Places API key{" "}
              {gcfg?.configured && <span className="text-emerald-600">✓ saved ({gcfg.api_key_masked})</span>}
            </label>
            <div className="flex gap-2">
              <input
                type="password"
                placeholder={gcfg?.configured ? "Paste a new key to replace the saved one" : "Paste your API key (AIza...)"}
                value={apiKeyInput}
                onChange={e => setApiKeyInput(e.target.value)}
                className="flex-1 px-3 py-2.5 bg-white border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-sm placeholder:text-[#3A4557] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/30"
                data-testid="google-api-key-input"
              />
              <Button onClick={saveApiKey} disabled={gBusy || !apiKeyInput.trim()} className="btn-gold h-10" data-testid="google-api-key-save-btn">
                {gBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save Key
              </Button>
            </div>
            <button onClick={() => setShowKeySteps(!showKeySteps)} className="text-[#A87608] text-xs mt-2 underline" data-testid="google-key-steps-toggle">
              How do I get an API key? (2 minutes, free)
            </button>
            {showKeySteps && (
              <ol className="text-[#2E3B57] text-xs mt-2 space-y-1.5 list-decimal list-inside bg-[#EDF1F8]/30 rounded-xl p-3" data-testid="google-key-steps">
                <li>Open <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-[#A87608] underline">console.cloud.google.com</a> and sign in with your Google account</li>
                <li>Create a project (any name, e.g. "Kavach Box Reviews") — attach billing (Google gives ample free monthly credit; this usage stays free)</li>
                <li>Go to <span className="text-[#1B2A4A]">APIs &amp; Services → Library</span> → search <span className="text-[#1B2A4A]">"Places API (New)"</span> → Enable</li>
                <li>Go to <span className="text-[#1B2A4A]">APIs &amp; Services → Credentials → Create credentials → API key</span> → copy it</li>
                <li>Paste the key above and press Save Key — it is stored on your server only, never exposed publicly</li>
              </ol>
            )}
          </div>

          {/* Step 2: business */}
          {gcfg?.configured && (
            <div className="mb-4">
              <label className="text-[#3A4557] text-xs block mb-1">Step 2 — Your business on Google</label>
              {gcfg.place_id ? (
                <div className="flex items-center justify-between gap-3 bg-[#EDF1F8]/40 border border-[#C9D4E6] rounded-xl p-3">
                  <div>
                    <p className="text-[#1B2A4A] text-sm font-medium flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-emerald-600" /> {gcfg.place_name || "Business linked"}
                    </p>
                    {gcfg.place_address && <p className="text-[#3A4557] text-xs mt-0.5">{gcfg.place_address}</p>}
                  </div>
                  <Button variant="outline" onClick={() => setGcfg({ ...gcfg, place_id: "" })} className="btn-outline h-8 text-xs" data-testid="google-change-business-btn">
                    Change
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <input
                      value={bizQuery}
                      onChange={e => setBizQuery(e.target.value)}
                      placeholder='Business name + city (e.g. "Kavach Box Ahmedabad")'
                      className="flex-1 px-3 py-2.5 bg-white border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-sm placeholder:text-[#3A4557] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/30"
                      data-testid="google-biz-query-input"
                    />
                    <Button onClick={findBusiness} disabled={gBusy} className="btn-gold h-10" data-testid="google-find-btn">
                      {gBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-1" />} Find
                    </Button>
                  </div>
                  {candidates.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {candidates.map(c => (
                        <button
                          key={c.place_id}
                          onClick={() => selectBusiness(c)}
                          className="w-full text-left bg-[#EDF1F8]/40 border border-[#C9D4E6] hover:border-[#D4A017]/50 rounded-xl p-3 transition-colors"
                          data-testid={`google-candidate-${c.place_id}`}
                        >
                          <p className="text-[#1B2A4A] text-sm font-medium">{c.name}</p>
                          <p className="text-[#3A4557] text-xs">{c.address}</p>
                          {c.rating && <p className="text-[#A87608] text-xs mt-0.5">★ {c.rating} · {c.review_count} reviews</p>}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 3: sync */}
          {gcfg?.configured && gcfg?.place_id && (
            <div className="flex items-center gap-3 flex-wrap">
              <Button onClick={syncNow} disabled={gBusy} className="btn-gold h-9" data-testid="google-sync-now-btn">
                {gBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Sync Now
              </Button>
              {gcfg.last_synced_at && (
                <span className="text-[#3A4557] text-xs" data-testid="google-last-synced">
                  Last synced: {new Date(gcfg.last_synced_at).toLocaleString()}
                </span>
              )}
            </div>
          )}
          {gcfg?.last_sync_error && (
            <p className="text-red-600 text-xs mt-2" data-testid="google-sync-error">Last sync failed: {gcfg.last_sync_error}</p>
          )}
        </div>

        {/* Settings */}
        <div className="glass-card p-5" data-testid="reviews-settings-card">
          <div className="flex items-center gap-2 mb-4">
            <Settings2 className="h-4 w-4 text-[#A87608]" />
            <h2 className="text-[#1B2A4A] font-semibold text-sm">Google Rating Settings</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="text-[#3A4557] text-xs block mb-1">Overall rating (1–5)</label>
              <input
                type="number" min="1" max="5" step="0.1" value={settings.google_rating}
                onChange={e => setSettings({ ...settings, google_rating: e.target.value })}
                className="w-full px-3 py-2.5 bg-white border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A017]/30"
                data-testid="settings-rating-input"
              />
            </div>
            <div>
              <label className="text-[#3A4557] text-xs block mb-1">Total review count</label>
              <input
                type="number" min="0" value={settings.google_review_count}
                onChange={e => setSettings({ ...settings, google_review_count: e.target.value })}
                className="w-full px-3 py-2.5 bg-white border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A017]/30"
                data-testid="settings-count-input"
              />
            </div>
            <div>
              <label className="text-[#3A4557] text-xs block mb-1">Google reviews link</label>
              <input
                type="text" placeholder="https://g.page/..." value={settings.google_reviews_url}
                onChange={e => setSettings({ ...settings, google_reviews_url: e.target.value })}
                className="w-full px-3 py-2.5 bg-white border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-sm placeholder:text-[#3A4557] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/30"
                data-testid="settings-url-input"
              />
            </div>
          </div>
          <p className="text-[#3A4557] text-xs mb-3">
            Once your Google Business Profile is approved, paste its review link here — visitors will be able to jump straight to your live Google reviews.
          </p>
          <Button onClick={saveSettings} disabled={saving} className="btn-gold h-9" data-testid="settings-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save Settings
          </Button>
        </div>

        {/* Add review */}
        <div className="glass-card p-5">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-[#1B2A4A] font-semibold text-sm">{reviews.length} Reviews on Website</h2>
            {!showAdd && (
              <Button onClick={() => setShowAdd(true)} className="btn-gold h-9" data-testid="add-review-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Review
              </Button>
            )}
          </div>
          <p className="text-[#3A4557] text-xs mb-4">
            Paste each new Google review here — it appears in the landing page carousel instantly.
          </p>
          {showAdd && (
            <div className="border border-[#D4A017]/30 rounded-xl p-4 mb-4 bg-[#D4A017]/5">
              <ReviewForm initial={EMPTY_FORM} onSave={addReview} onCancel={() => setShowAdd(false)} saving={saving} />
            </div>
          )}

          {/* Review list */}
          <div className="space-y-3">
            {reviews.map(review => (
              <div key={review.id} className="border border-[#C9D4E6] rounded-xl p-4" data-testid={`review-item-${review.id}`}>
                {editingId === review.id ? (
                  <ReviewForm
                    initial={{ reviewer_name: review.reviewer_name, rating: review.rating, text: review.text, review_date: review.review_date || "" }}
                    onSave={(form) => updateReview(review.id, form)}
                    onCancel={() => setEditingId(null)}
                    saving={saving}
                  />
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[#1B2A4A] font-semibold text-sm">{review.reviewer_name}</span>
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map(i => (
                            <Star key={i} className={`h-3.5 w-3.5 ${i <= (review.rating || 5) ? "text-[#A87608] fill-[#D4A017]" : "text-[#3A4557]"}`} />
                          ))}
                        </div>
                        {review.review_date && <span className="text-[#3A4557] text-xs">{review.review_date}</span>}
                        {review.sample && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 font-medium">
                            Sample — replace with real review
                          </span>
                        )}
                        {review.source === "google_api" && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 font-medium" data-testid={`google-live-badge-${review.id}`}>
                            Live · Synced from Google
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {review.source !== "google_api" && (
                          <button onClick={() => setEditingId(review.id)} className="p-2 rounded-lg hover:bg-[#EDF1F8] text-[#3A4557] hover:text-[#A87608]" data-testid={`edit-review-${review.id}`}>
                            <Pencil className="h-4 w-4" />
                          </button>
                        )}
                        <button onClick={() => deleteReview(review.id)} className="p-2 rounded-lg hover:bg-red-500/10 text-[#3A4557] hover:text-red-600" data-testid={`delete-review-${review.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-[#2E3B57] text-sm">"{review.text}"</p>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 text-[#A87608] text-sm font-medium hover:underline"
          data-testid="preview-landing-link"
        >
          <ExternalLink className="h-4 w-4" /> Preview on landing page
        </a>
      </main>
      <MobileBottomNav onMenuClick={() => setShowSidebar(true)} />
      <MobileSidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />
    </div>
  );
}
